import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

// [4.9.3] Field-log of labour hours worked on an obra, imputing cost to the work.
export interface WorkLaborEntry {
  id: number;
  workId: number;
  workName: string;
  workerId: number | null;
  workerName: string;
  workedOn: string;
  hours: number;
  hourlyRate: number | null;
  cost: number;
  isValidated: boolean;
  validatedAt: string | null;
  validatedByUserId: string | null;
  notes: string | null;
  created: string;
  lastModified: string;
}

export interface WorkLaborEntryPayload {
  workId: number;
  workerId?: number | null;
  workerName: string;
  workedOn: string;
  hours: number;
  hourlyRate?: number | null;
  notes?: string | null;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  return (await response.json()) as T;
}

export function getWorkLaborEntries(
  workId: number,
): Promise<WorkLaborEntry[]> {
  return request<WorkLaborEntry[]>(
    `/api/work-labor-entries?workId=${workId}`,
  );
}

export function createWorkLaborEntry(
  payload: WorkLaborEntryPayload,
): Promise<WorkLaborEntry> {
  return request<WorkLaborEntry>("/api/work-labor-entries", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateWorkLaborEntry(
  id: number,
  payload: WorkLaborEntryPayload,
): Promise<WorkLaborEntry> {
  return request<WorkLaborEntry>(`/api/work-labor-entries/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function validateWorkLaborEntry(
  id: number,
  isValidated: boolean,
): Promise<WorkLaborEntry> {
  return request<WorkLaborEntry>(`/api/work-labor-entries/${id}/validate`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ isValidated }),
  });
}

export async function deleteWorkLaborEntry(id: number): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl}/api/work-labor-entries/${id}`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}
